import { formatDistanceToNow } from 'date-fns';
import {
  LEGACY_SCREENSHOT_STATUS_CODES,
  SCREENSHOT_STATUS_LABELS,
  ScreenshotStatus,
  ScreenshotTimelineEntry,
} from '../../types/ScreenshotTypes';

/**
 * `created` is an offset-qualified UTC instant since 2026-09-21 ("…T14:53:44Z").
 * Earlier rows were naive ("2026-09-18T14:53:44"), which `new Date()` reads as
 * LOCAL time (a fresh capture showed "2 hours ago" in Paris), so a missing
 * offset is still treated as UTC.
 */
export const parseScreenshotDate = (value: string | null): Date | null => {
  if (!value) return null;
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
  const date = new Date(hasOffset ? value : `${value.trim()}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "3 days ago", or an empty string for legacy rows without a timestamp. */
export const screenshotRelativeTime = (created: string | null): string => {
  const date = parseScreenshotDate(created);
  if (!date) return '';
  return formatDistanceToNow(date, { addSuffix: true });
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Exact capture instant in UTC, same shape as the status history table:
 * "18-09-2026, 14:53:44 UTC". Empty string when the timestamp is missing.
 */
export const screenshotUtcTime = (created: string | null): string => {
  const date = parseScreenshotDate(created);
  if (!date) return '';
  return `${pad2(date.getUTCDate())}-${pad2(
    date.getUTCMonth() + 1,
  )}-${date.getUTCFullYear()}, ${pad2(date.getUTCHours())}:${pad2(
    date.getUTCMinutes(),
  )}:${pad2(date.getUTCSeconds())} UTC`;
};

/**
 * Caption shown under a capture, relative and exact:
 * "Captured 3 days ago · 18-09-2026, 14:53:44 UTC · Threat confirmed".
 */
export const screenshotCaption = (
  entry: Pick<ScreenshotTimelineEntry, 'created' | 'status'>,
): string => {
  const when = screenshotRelativeTime(entry.created);
  const exact = screenshotUtcTime(entry.created);
  const parts = [
    when ? `Captured ${when}` : 'Captured',
    exact,
    screenshotStatusLabel(entry.status),
  ].filter(Boolean);
  return parts.join(' · ');
};

/**
 * Display wording for a capture reason. Accepts the current codes and the
 * pre-2026-09-22 labels old rows still carry; anything else (a code added
 * later) is shown as it comes.
 */
export const screenshotStatusLabel = (
  status: string | null | undefined,
): string => {
  if (!status) return '';
  const code = LEGACY_SCREENSHOT_STATUS_CODES[status] ?? status;
  return SCREENSHOT_STATUS_LABELS[code as ScreenshotStatus] ?? status;
};
